import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Truck, RotateCcw, X, Star, Info, AlertTriangle } from 'lucide-react';

const TierComparisonModal = ({ isOpen, onClose, vehicleInfo }) => {
  if (!isOpen) return null;

  const tiers = [
    {
      id: 'new',
      name: 'New Parts',
      icon: <CheckCircle className="text-blue-600" size={24} />,
      color: 'blue',
      quality: 10,
      price: 'Highest',
      longevity: '5-10+ Years',
      warranty: 'Standard OEM',
      idealFor: 'Newer cars (< 3 years), critical engine parts, long-term ownership.',
      valueScore: 8,
    },
    {
      id: 'aftermarket',
      name: 'Aftermarket',
      icon: <Truck className="text-green-600" size={24} />,
      color: 'green',
      quality: 8,
      price: 'Medium',
      longevity: '3-7 Years',
      warranty: 'Brand Specific',
      idealFor: 'Daily drivers, cost-conscious maintenance, older models.',
      valueScore: 10,
    },
    {
      id: 'used',
      name: 'Certified Used',
      icon: <RotateCcw className="text-amber-500" size={24} />,
      color: 'amber',
      quality: 7,
      price: 'Lowest',
      longevity: 'Varies',
      warranty: 'Sparepe Assured',
      idealFor: 'Discontinued parts, maximum savings, external body parts.',
      valueScore: 9,
    }
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 30 }}
          className="bg-white rounded-[2.5rem] shadow-2xl max-w-4xl w-full overflow-hidden flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <div>
              <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter">Tier Comparison</h2>
              <p className="text-gray-500 text-sm font-medium">Find the perfect balance for your {vehicleInfo || 'vehicle'}</p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
              {tiers.map((tier) => (
                <div key={tier.id} className={`border-2 rounded-3xl p-6 ${tier.id === 'aftermarket' ? 'border-green-600 shadow-xl shadow-green-500/5 bg-green-50/5' : 'border-gray-100 bg-white'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${tier.color}-100/50`}>
                      {tier.icon}
                    </div>
                    <div>
                      <h4 className="font-black text-gray-900 uppercase tracking-tighter">{tier.name}</h4>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={10} className={i < tier.valueScore/2 ? `fill-${tier.color}-500 text-${tier.color}-500` : 'text-gray-200'} />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400 font-bold uppercase">Quality</span>
                      <span className="text-gray-900 font-black">{tier.quality}/10</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400 font-bold uppercase">Price Index</span>
                      <span className="text-gray-900 font-black">{tier.price}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400 font-bold uppercase">Est. Longevity</span>
                      <span className="text-gray-900 font-black">{tier.longevity}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-4">
                    <p className="text-[11px] text-gray-600 font-medium leading-relaxed italic">
                      "Best for {tier.idealFor}"
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Recommendation Logic Section */}
            <div className="bg-[#131c36] text-white rounded-[2rem] p-8 relative overflow-hidden">
               <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-20 -mt-20" />
               <h3 className="text-xl font-black uppercase tracking-tighter mb-6 flex items-center gap-2 relative z-10">
                 <Star className="text-yellow-400 fill-yellow-400" /> Sparepe Smart Suggestion
               </h3>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
                  <div className="space-y-4">
                     <div className="flex items-start gap-3">
                        <div className="p-2 bg-white/10 rounded-lg shrink-0 mt-1">
                           <CheckCircle size={18} className="text-blue-400" />
                        </div>
                        <div>
                           <p className="font-bold text-sm">Critical Components? GO NEW.</p>
                           <p className="text-xs text-blue-200">For brakes, engine internals, and steering, original parts offer unmatched safety and lifespan.</p>
                        </div>
                     </div>
                     <div className="flex items-start gap-3">
                        <div className="p-2 bg-white/10 rounded-lg shrink-0 mt-1">
                           <Truck size={18} className="text-green-400" />
                        </div>
                        <div>
                           <p className="font-bold text-sm">Best Value for Money? GO AFTERMARKET.</p>
                           <p className="text-xs text-green-200">Brands like Bosch, Valeo, and Mahle offer 95% of OEM quality at 60-70% of the price.</p>
                        </div>
                     </div>
                  </div>
                  <div className="space-y-4">
                     <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                           <Info size={16} className="text-amber-400" />
                           <span className="text-sm font-black uppercase">Expert Verdict</span>
                        </div>
                        <p className="text-xs text-gray-300 leading-relaxed">
                          For your {vehicleInfo || 'vehicle'}, we recommend <strong>Aftermarket</strong> for regular maintenance and 
                          <strong> New </strong> for mechanical integrity. Only consider <strong>Used</strong> for aesthetic body panels or rare parts.
                        </p>
                     </div>
                  </div>
               </div>
            </div>
          </div>

          <div className="px-8 py-6 border-t border-gray-100 bg-gray-50/50 flex justify-center">
             <button 
               onClick={onClose}
               className="bg-[#131c36] text-white px-8 py-3 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-black transition-all"
             >
               Got it, thanks!
             </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TierComparisonModal;
